import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { Modal } from 'components/ui/Modal'
import { Checkbox } from 'components/ui/Checkbox'
import { Badge } from 'components/ui/Badge'
import { ChevronLeft, ChevronRight, Check, User, Lock, Shield, Mail, UserCircle, KeyRound, Users, Settings, Eye, EyeOff } from 'lucide-react'
import { 
  userManagementApi,
  rolesApi,
  type CreateUserData,
  type UpdateUserData,
  type UserWithRoles,
  type User as UserType,
  type Role
} from 'lib/api'
import { useToast } from 'contexts/ToastContext'
import { useAuth } from 'contexts/AuthContext'

interface UserFormProps {
  isOpen: boolean
  onClose: () => void
  user?: UserWithRoles | null
  onSuccess: () => void
}

interface FormData {
  email: string
  username: string
  password: string
  confirmPassword: string
  first_name: string
  last_name: string
  full_name: string
  is_active: boolean
  is_staff: boolean
  is_superuser: boolean
}

const STEPS = {
  BASIC_INFO: 0,
  SECURITY: 1,
  ROLES_PERMISSIONS: 2
}

export function UserForm({ isOpen, onClose, user, onSuccess }: UserFormProps) {
  const { t } = useTranslation()
  const { addToast } = useToast()
  const { user: currentUser } = useAuth()
  const [loading, setLoading] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [emailError, setEmailError] = useState<string>('')
  const [firstNameError, setFirstNameError] = useState<string>('')
  const [lastNameError, setLastNameError] = useState<string>('')
  const [usernameError, setUsernameError] = useState<string>('')
  const [passwordError, setPasswordError] = useState<string>('')
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>('')
  const [rolesError, setRolesError] = useState<string>('')
  const [roles, setRoles] = useState<Role[]>([])
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null)
  const [userDetails, setUserDetails] = useState<UserType | null>(null)
  const [loadingUserDetails, setLoadingUserDetails] = useState(false)
  
  const initialFormState: FormData = {
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    full_name: '',
    is_active: true,
    is_staff: false,
    is_superuser: false
  }

  const [formData, setFormData] = useState<FormData>(initialFormState)

  const isEditMode = !!user

  // Calculate total steps (skip security step in edit mode)
  const totalSteps = isEditMode ? 2 : 3
  const stepConfig = isEditMode 
    ? [
        { name: t('users.form.stepBasicInfo'), icon: User, description: t('users.form.stepBasicInfoDesc') },
        { name: t('users.form.stepRolesPermissions'), icon: Shield, description: t('users.form.stepRolesPermissionsDesc') }
      ]
    : [
        { name: t('users.form.stepBasicInfo'), icon: User, description: t('users.form.stepBasicInfoDesc') },
        { name: t('users.form.stepSecurity'), icon: Lock, description: t('users.form.stepSecurityDesc') },
        { name: t('users.form.stepRolesPermissions'), icon: Shield, description: t('users.form.stepRolesPermissionsDesc') }
      ]

  // Reset form when modal opens/closes or user changes
  React.useEffect(() => {
    const loadData = async () => {
      if (isOpen) {
        setCurrentStep(0)
        setShowPassword(false)
        setShowConfirmPassword(false)
        // Load available roles for assignment
        rolesApi.getRoles()
          .then(res => {
            const list = Array.isArray(res.data) ? res.data : (res.data?.results || [])
            // Filter out system roles for organization users (non-superusers)
            // Organization users should only see organization roles (Admin, HR Manager, Recruiter, Viewer, etc.)
            const isSuperuser = (currentUser as any)?.is_superuser || false
            const filteredRoles = isSuperuser 
              ? list 
              : list.filter(role => {
                  // Include organization roles (role_type='organization' or not set)
                  // Exclude system roles (role_type='system' OR is_system_role=true)
                  const isSystemRole = role.role_type === 'system' || role.is_system_role === true
                  return !isSystemRole
                })
            setRoles(filteredRoles)
          })
          .catch(() => setRoles([]))

        if (user) {
          // Edit mode - fetch full user details to get username
          setLoadingUserDetails(true)
          try {
            const userDetailsRes = await userManagementApi.getUser(user.id)
            const fullUserDetails = userDetailsRes.data
            setUserDetails(fullUserDetails)
          
          // Populate form with complete user data
          setFormData({
            email: fullUserDetails.email,
            username: fullUserDetails.username || fullUserDetails.email,
            password: '',
            confirmPassword: '',
            first_name: fullUserDetails.first_name || '',
            last_name: fullUserDetails.last_name || '',
            full_name: fullUserDetails.full_name || '',
            // Use actual status flags from the user object when available
            is_active: fullUserDetails.is_active ?? true,
            is_staff: fullUserDetails.is_staff ?? false,
            is_superuser: fullUserDetails.is_superuser ?? false
          })
        } catch (error) {
          console.error('Error fetching user details:', error)
          // Fallback to user prop data if fetch fails
          setFormData({
            email: user.email,
            username: user.username || user.email,
            password: '',
            confirmPassword: '',
            first_name: user.full_name?.split(' ')[0] || '',
            last_name: user.full_name?.split(' ').slice(1).join(' ') || '',
            full_name: user.full_name || '',
            is_active: (user as any).is_active ?? true,
            is_staff: (user as any).is_staff ?? false,
            is_superuser: (user as any).is_superuser ?? false
          })
        } finally {
          setLoadingUserDetails(false)
        }
        
        // Pre-select the user's first role if available
        const userRoles = Array.isArray((user as any).roles) ? (user as any).roles : []
        setSelectedRoleId(userRoles.length > 0 ? userRoles[0].role : null)
        setEmailError('')
        setFirstNameError('')
        setLastNameError('')
        setUsernameError('')
        setPasswordError('')
        setConfirmPasswordError('')
      } else {
        // Create mode - reset form
        setFormData(initialFormState)
        setSelectedRoleId(null)
        setEmailError('')
        setFirstNameError('')
        setLastNameError('')
        setUsernameError('')
        setPasswordError('')
        setConfirmPasswordError('')
        }
      }
    }
    
    loadData()
  }, [isOpen, user, currentUser])

  const validateStep = (step: number): boolean => {
    if (step === STEPS.BASIC_INFO) {
      // Validate basic info - check all required fields
      let isValid = true
      const errors: string[] = []

      // Validate first name
      if (!formData.first_name.trim()) {
        setFirstNameError(t('users.form.required'))
        errors.push(t('users.firstName'))
        isValid = false
      } else {
        setFirstNameError('')
      }

      // Validate last name
      if (!formData.last_name.trim()) {
        setLastNameError(t('users.form.required'))
        errors.push(t('users.lastName'))
        isValid = false
      } else {
        setLastNameError('')
      }

      // Validate email
      if (!formData.email.trim()) {
        setEmailError(t('users.form.required'))
        errors.push(t('users.email'))
        isValid = false
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(formData.email)) {
          setEmailError(t('users.messages.invalidEmail'))
          errors.push(t('users.email'))
          isValid = false
        } else {
          setEmailError('')
        }
      }

      // Validate username
      if (!formData.username.trim()) {
        setUsernameError(t('users.form.required'))
        errors.push(t('users.username'))
        isValid = false
      } else {
        setUsernameError('')
      }

      return isValid
    }

    if (step === STEPS.SECURITY && !isEditMode) {
      // Validate security - Always show errors when validating
      let isValid = true
      
      // Validate password
      if (!formData.password) {
        setPasswordError(t('users.form.required'))
        isValid = false
      } else if (formData.password.length < 8) {
        setPasswordError(t('users.messages.passwordTooShort'))
        isValid = false
      } else {
        // Check for common passwords (basic check)
        const commonPasswords = ['password', '12345678', '123456789', 'qwerty', 'abc123', 'password123', 'admin', 'letmein']
        if (commonPasswords.includes(formData.password.toLowerCase())) {
          setPasswordError(t('users.form.passwordTooCommon'))
          isValid = false
        } else {
          setPasswordError('')
        }
      }

      // Validate confirm password
      if (!formData.confirmPassword) {
        setConfirmPasswordError(t('users.form.required'))
        isValid = false
      } else if (formData.password !== formData.confirmPassword) {
        setConfirmPasswordError(t('users.messages.passwordMismatch'))
        isValid = false
      } else {
        setConfirmPasswordError('')
      }

      return isValid
    }

    if (step === STEPS.ROLES_PERMISSIONS) {
      // Validate that a role is selected
      if (!selectedRoleId) {
        setRolesError(t('users.form.roleRequired'))
        return false
      } else {
        setRolesError('')
        return true
      }
    }

    return true
  }

  const validateForm = (): string | null => {
    let firstError: string | null = null
    
    // Validate first name
    if (!formData.first_name.trim()) {
      const msg = t('users.form.required')
      setFirstNameError(msg)
      firstError = firstError || msg
    } else {
      setFirstNameError('')
    }

    // Validate last name
    if (!formData.last_name.trim()) {
      const msg = t('users.form.required')
      setLastNameError(msg)
      firstError = firstError || msg
    } else {
      setLastNameError('')
    }

    // Email
    if (!formData.email.trim()) {
      const msg = t('users.form.required')
      setEmailError(msg)
      firstError = firstError || msg
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.email)) {
        const msg = t('users.messages.invalidEmail')
        setEmailError(msg)
        firstError = firstError || msg
      } else {
        setEmailError('')
      }
    }

    // Username
    if (!formData.username.trim()) {
      const msg = t('users.form.required')
      setUsernameError(msg)
      firstError = firstError || msg
    } else {
      setUsernameError('')
    }

    if (!isEditMode) {
      // Password
      if (!formData.password) {
        const msg = t('users.form.required')
        setPasswordError(msg)
        firstError = firstError || msg
      } else if (formData.password.length < 8) {
        const msg = t('users.messages.passwordTooShort')
        setPasswordError(msg)
        firstError = firstError || msg
      } else {
        setPasswordError('')
      }
      // Confirm password
      if (!formData.confirmPassword) {
        const msg = t('users.form.required')
        setConfirmPasswordError(msg)
        firstError = firstError || msg
      } else if (formData.password !== formData.confirmPassword) {
        const msg = t('users.messages.passwordMismatch')
        setConfirmPasswordError(msg)
        firstError = firstError || msg
      } else {
        setConfirmPasswordError('')
      }
    }

    // Validate roles
    if (!selectedRoleId) {
      const roleErrorMsg = t('users.form.roleRequired')
      setRolesError(roleErrorMsg)
      firstError = firstError || roleErrorMsg
    } else {
      setRolesError('')
    }

    return firstError
  }

  const handleNext = () => {
    const isValid = validateStep(currentStep)
    if (isValid) {
      // Adjust step index for edit mode (skip security step)
      if (isEditMode && currentStep === STEPS.BASIC_INFO) {
        setCurrentStep(STEPS.ROLES_PERMISSIONS)
      } else {
        setCurrentStep(prev => Math.min(prev + 1, totalSteps - 1))
      }
    } else {
      // Don't show toast, errors are already displayed in the form
      // Just scroll to first error if needed
      const firstErrorElement = document.querySelector('.border-destructive')
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }

  const handlePrevious = () => {
    // Adjust step index for edit mode (skip security step)
    if (isEditMode && currentStep === STEPS.ROLES_PERMISSIONS) {
      setCurrentStep(STEPS.BASIC_INFO)
    } else {
      setCurrentStep(prev => Math.max(prev - 1, 0))
    }
  }

  const handleSubmit = async () => {
    const validationError = validateForm()
    if (validationError) {
      addToast({
        type: 'error',
        title: t('common.error'),
        message: validationError,
        duration: 5000
      })
      return
    }

    setLoading(true)
    try {
      if (isEditMode && user) {
        // Update user
        const updateData: UpdateUserData = {
          email: formData.email,
          username: formData.username,
          first_name: formData.first_name,
          last_name: formData.last_name,
          full_name: formData.full_name || `${formData.first_name} ${formData.last_name}`.trim(),
          is_active: formData.is_active,
          is_staff: formData.is_staff,
          is_superuser: formData.is_superuser
        }

        await userManagementApi.updateUser(user.id, updateData)

        // Assign role (always assign, even if null)
        if (selectedRoleId) {
          await rolesApi.assignRolesToUser({ user_id: user.id, role_ids: [selectedRoleId] })
        }

        addToast({
          type: 'success',
          title: t('common.success'),
          message: t('users.messages.userUpdated'),
          duration: 3000
        }) } else {
        // Create user
        const createData: CreateUserData = {
          email: formData.email,
          username: formData.username || formData.email,
          password: formData.password,
          password_confirm: formData.confirmPassword,
          first_name: formData.first_name,
          last_name: formData.last_name,
          full_name: formData.full_name || `${formData.first_name} ${formData.last_name}`.trim(),
          is_active: formData.is_active,
          is_staff: formData.is_staff,
          is_superuser: formData.is_superuser
        }

        const resp = await userManagementApi.createUser(createData)

        // Assign role
        const createdUserId = (resp as any)?.data?.id || (resp as any)?.data?.user?.id
        if (createdUserId && selectedRoleId) {
          await rolesApi.assignRolesToUser({ user_id: createdUserId, role_ids: [selectedRoleId] })
        }
        addToast({
          type: 'success',
          title: t('common.success'),
          message: t('users.messages.userCreated'),
          duration: 3000
        })
      }

      onSuccess()
      onClose()
    } catch (error: any) {
      console.error('Error saving user:', error)
      // Try to extract DRF validation errors and set them in the appropriate fields
      const data = error?.response?.data
      
      if (data && typeof data === 'object') {
        // Handle field-specific errors
        if (data.first_name) {
          const errorMsg = Array.isArray(data.first_name) ? data.first_name[0] : data.first_name
          setFirstNameError(errorMsg)
          // Go back to basic info step if we're not there
          if (currentStep !== STEPS.BASIC_INFO) {
            setCurrentStep(STEPS.BASIC_INFO)
          }
        }
        if (data.last_name) {
          const errorMsg = Array.isArray(data.last_name) ? data.last_name[0] : data.last_name
          setLastNameError(errorMsg)
          if (currentStep !== STEPS.BASIC_INFO) {
            setCurrentStep(STEPS.BASIC_INFO)
          }
        }
        // Handle email errors (including uniqueness errors)
        if (data.email) {
          const errorMsg = Array.isArray(data.email) ? data.email[0] : data.email
          // Check for various uniqueness error messages
          const lowerMsg = errorMsg.toLowerCase()
          if (lowerMsg.includes('already exists') || 
              lowerMsg.includes('unique') || 
              lowerMsg.includes('user with this email') ||
              lowerMsg.includes('this field must be unique')) {
            setEmailError(t('users.form.emailAlreadyExists'))
          } else {
            setEmailError(errorMsg)
          }
          if (currentStep !== STEPS.BASIC_INFO) {
            setCurrentStep(STEPS.BASIC_INFO)
          }
        }
        
        // Handle username errors (including uniqueness errors)
        if (data.username) {
          const errorMsg = Array.isArray(data.username) ? data.username[0] : data.username
          // Check for various uniqueness error messages
          const lowerMsg = errorMsg.toLowerCase()
          if (lowerMsg.includes('already exists') || 
              lowerMsg.includes('unique') || 
              lowerMsg.includes('user with that username') ||
              lowerMsg.includes('this field must be unique')) {
            setUsernameError(t('users.form.usernameAlreadyTaken'))
          } else {
            setUsernameError(errorMsg)
          }
          if (currentStep !== STEPS.BASIC_INFO) {
            setCurrentStep(STEPS.BASIC_INFO)
          }
        }
        
        // Handle duplicate email/username errors from server (non_field_errors)
        if (data.non_field_errors) {
          const errorMsg = Array.isArray(data.non_field_errors) ? data.non_field_errors[0] : data.non_field_errors
          const lowerMsg = errorMsg.toLowerCase()
          if (lowerMsg.includes('email') || lowerMsg.includes('user with this email')) {
            setEmailError(t('users.form.emailAlreadyExists'))
            if (currentStep !== STEPS.BASIC_INFO) {
              setCurrentStep(STEPS.BASIC_INFO)
            }
          }
          if (lowerMsg.includes('username') || lowerMsg.includes('user with this username') || lowerMsg.includes('user with that username')) {
            setUsernameError(t('users.form.usernameAlreadyTaken'))
            if (currentStep !== STEPS.BASIC_INFO) {
              setCurrentStep(STEPS.BASIC_INFO)
            }
          }
        }
        if (data.password) {
          const errorMsg = Array.isArray(data.password) ? data.password[0] : data.password
          setPasswordError(errorMsg)
          // Go back to security step if we're not there
          if (currentStep !== STEPS.SECURITY && !isEditMode) {
            setCurrentStep(STEPS.SECURITY)
          }
        }
        if (data.password_confirm) {
          const errorMsg = Array.isArray(data.password_confirm) 
            ? data.password_confirm[0] 
            : data.password_confirm
          setConfirmPasswordError(errorMsg)
          if (currentStep !== STEPS.SECURITY && !isEditMode) {
            setCurrentStep(STEPS.SECURITY)
          }
        }
        
        // Show general error message if there are other errors
        const errorFields = ['first_name', 'last_name', 'email', 'username', 'password', 'password_confirm', 'non_field_errors']
        const hasOtherErrors = Object.keys(data).some(key => !errorFields.includes(key))
        if (hasOtherErrors) {
        const parts: string[] = []
        Object.entries(data).forEach(([field, value]) => {
            if (!errorFields.includes(field)) {
          if (Array.isArray(value)) {
            parts.push(`${field}: ${value.join(', ')}`)
          } else if (typeof value === 'string') {
            parts.push(`${field}: ${value}`)
          }
            }
          })
          if (parts.length) {
            addToast({
              type: 'error',
              title: t('common.error'),
              message: parts.join(' | '),
              duration: 5000
            })
          }
        } else {
          // Scroll to first error
          setTimeout(() => {
            const firstErrorElement = document.querySelector('.border-destructive')
            if (firstErrorElement) {
              firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
          }, 100)
        }
      } else {
        // Generic error
        let message = t('users.saveUserError')
        if (typeof data === 'string') {
          message = data
      }
      addToast({
        type: 'error',
        title: t('common.error'),
        message,
        duration: 5000
      })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      setCurrentStep(0)
      onClose()
    }
  }

  const renderStepIndicator = () => {
    return (
      <div className="relative mb-8">
        <div className="flex items-start">
          {stepConfig.map((step, index) => {
            const isActive = index === currentStep
            const isCompleted = index < currentStep
            const Icon = step.icon
            const isLast = index === stepConfig.length - 1

  return (
              <React.Fragment key={index}>
                <div className="flex flex-col items-center flex-1 relative">
                  {/* Step Circle */}
                  <div className="relative z-10">
                    <div
                      className={`flex items-center justify-center w-12 h-12 rounded-full border-2 ${
                        isActive
                          ? 'bg-primary text-primary-foreground border-primary'
                          : isCompleted
                          ? 'bg-primary/10 text-primary border-primary'
                          : 'bg-background text-muted-foreground border-muted'
                      }`}
                    >
                      {isCompleted ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <Icon className={`w-5 h-5 ${isActive ? 'text-primary-foreground' : ''}`} />
                      )}
                    </div>
                  </div>
                  
                  {/* Step Label */}
                  <div className="mt-3 text-center max-w-[140px] px-2">
                    <div className={`text-sm font-semibold ${
                      isActive ? 'text-primary' : isCompleted ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {step.name}
                    </div>
                    <div className={`text-xs mt-1 ${
                      isActive ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {step.description}
                    </div>
                  </div>
                </div>
                
                {/* Connector Line - Centered vertically with the circle (24px from top = center of 48px circle) */}
                {!isLast && (
                  <div className="flex-1 relative mx-2" style={{ height: '48px' }}>
                    <div className="absolute left-0 right-0 h-0.5 bg-muted" style={{ top: '50%', transform: 'translateY(-50%)' }} />
                    <div 
                      className={`absolute left-0 h-0.5 ${
                        isCompleted ? 'bg-primary' : 'bg-transparent'
                      }`}
                      style={{ 
                        width: isCompleted ? '100%' : '0%',
                        top: '50%',
                        transform: 'translateY(-50%)'
                      }}
                    />
                  </div>
                )}
              </React.Fragment>
            )
          })}
        </div>
      </div>
    )
  }

  const renderStepContent = () => {
    // Adjust step for edit mode
    const actualStep = isEditMode && currentStep === STEPS.ROLES_PERMISSIONS 
      ? STEPS.ROLES_PERMISSIONS 
      : currentStep

    switch (actualStep) {
      case STEPS.BASIC_INFO:
        return (
          <div className="space-y-6">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <UserCircle className="w-4 h-4 text-muted-foreground" />
                    {t('users.firstName')} *
                  </label>
            <Input
              value={formData.first_name}
              onChange={(e) => {
                const value = e.target.value
                setFormData(prev => ({ ...prev, first_name: value }))
                if (firstNameError && value.trim()) {
                  setFirstNameError('')
                }
              }}
              placeholder={t('users.form.firstNamePlaceholder')}
              disabled={loading}
              aria-invalid={!!firstNameError}
              className={firstNameError ? 'border-destructive focus:ring-destructive' : ''}
            />
            {firstNameError && <p className="mt-1 text-sm text-destructive flex items-center gap-1">{firstNameError}</p>}
          </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <UserCircle className="w-4 h-4 text-muted-foreground" />
                    {t('users.lastName')} *
                  </label>
            <Input
              value={formData.last_name}
              onChange={(e) => {
                const value = e.target.value
                setFormData(prev => ({ ...prev, last_name: value }))
                if (lastNameError && value.trim()) {
                  setLastNameError('')
                }
              }}
              placeholder={t('users.form.lastNamePlaceholder')}
              disabled={loading}
              aria-invalid={!!lastNameError}
              className={lastNameError ? 'border-destructive focus:ring-destructive' : ''}
            />
            {lastNameError && <p className="mt-1 text-sm text-destructive flex items-center gap-1">{lastNameError}</p>}
          </div>
        </div>
        
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
            {t('users.email')} *
          </label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => {
              const value = e.target.value
              setFormData(prev => ({ ...prev, email: value }))
              if (emailError) {
                if (!value.trim()) setEmailError(t('users.form.required'))
                else {
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
                  setEmailError(emailRegex.test(value) ? '' : t('users.messages.invalidEmail'))
                }
              }
            }}
            placeholder={t('users.form.emailPlaceholder')}
            disabled={loading}
            aria-invalid={!!emailError}
                    className={emailError ? 'border-destructive focus:ring-destructive' : ''}
          />
                {emailError && <p className="mt-1 text-sm text-destructive flex items-center gap-1">{emailError}</p>}
        </div>

              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
            {t('users.username')} *
          </label>
          <Input
            value={formData.username}
            onChange={(e) => {
              const value = e.target.value
              setFormData(prev => ({ ...prev, username: value }))
              if (usernameError && value.trim()) {
                setUsernameError('')
              }
            }}
            placeholder={t('users.form.usernamePlaceholder')}
            disabled={loading}
            aria-invalid={!!usernameError}
            className={usernameError ? 'border-destructive focus:ring-destructive' : ''}
          />
          {usernameError && <p className="mt-1 text-sm text-destructive flex items-center gap-1">{usernameError}</p>}
        </div>
            </div>
          </div>
        )

      case STEPS.SECURITY:
        return (
          <div className="space-y-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-foreground mb-1">{t('users.form.accountSecurity')}</h3>
              <p className="text-sm text-muted-foreground">{t('users.form.accountSecurityDesc')}</p>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <KeyRound className="w-4 h-4 text-muted-foreground" />
                {t('users.password')} *
              </label>
              <div className="relative">
                <Input
                  id="create-user-password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => {
                    const value = e.target.value
                    setFormData(prev => ({ ...prev, password: value }))
                    // Clear error when user starts typing
                    if (passwordError && value && value.length >= 8) {
                      setPasswordError('')
                    }
                    // Clear confirm password error if passwords match
                    if (confirmPasswordError && value === formData.confirmPassword) {
                      setConfirmPasswordError('')
                    }
                  }}
                  placeholder={t('users.form.passwordPlaceholder')}
                  disabled={loading}
                  aria-invalid={!!passwordError}
                  className={`${passwordError ? 'border-destructive focus:ring-destructive' : ''} pr-10`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => !prev)}
                  disabled={loading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground disabled:opacity-50"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {passwordError && <p className="mt-1 text-sm text-destructive flex items-center gap-1">{passwordError}</p>}
                <p className="text-xs text-muted-foreground mt-1">{t('users.form.passwordMinLength')}</p>
            </div>
            
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Lock className="w-4 h-4 text-muted-foreground" />
                {t('users.confirmPassword')} *
              </label>
              <div className="relative">
                <Input
                  id="create-user-confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => {
                    const value = e.target.value
                    setFormData(prev => ({ ...prev, confirmPassword: value }))
                    // Clear error when passwords match
                    if (confirmPasswordError && value === formData.password) {
                      setConfirmPasswordError('')
                    }
                  }}
                  placeholder={t('users.form.confirmPasswordPlaceholder')}
                  disabled={loading}
                  aria-invalid={!!confirmPasswordError}
                  className={`${confirmPasswordError ? 'border-destructive focus:ring-destructive' : ''} pr-10`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(prev => !prev)}
                  disabled={loading}
                  aria-label={showConfirmPassword ? 'Hide password confirmation' : 'Show password confirmation'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground disabled:opacity-50"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {confirmPasswordError && <p className="mt-1 text-sm text-destructive flex items-center gap-1">{confirmPasswordError}</p>}
              </div>
            </div>
          </div>
        )

      case STEPS.ROLES_PERMISSIONS:
        return (
          <div className="space-y-6">
        {/* Role selection */}
            <div className="space-y-3">
              <label className="text-sm font-medium flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                {t('users.roles')} *
              </label>
          {roles.length === 0 ? (
                <div className="border border-dashed rounded-lg p-6 text-center">
                  <Users className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{t('users.noRolesAvailable')}</p>
                </div>
          ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
              {roles.map((role) => {
                const isSelected = selectedRoleId === role.id
                const isSystemRole = role.is_system_role
                
                return (
                  <label 
                    key={role.id} 
                    className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer ${
                      isSelected ? 'bg-primary/10 border-primary' : 'hover:bg-muted/50'
                    } ${isSystemRole ? 'opacity-75' : ''}`}
                  >
                  <input
                    type="radio"
                    name="role-selection"
                      checked={isSelected}
                      onChange={() => {
                        setSelectedRoleId(role.id)
                        // Clear error when a role is selected
                        if (rolesError) {
                          setRolesError('')
                        }
                      }}
                    disabled={loading}
                    className="rounded"
                  />
                    <div 
                      className="w-3 h-3 rounded-full flex-shrink-0" 
                      style={{ backgroundColor: role.color }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className="font-medium">{role.name}</p>
                        {isSystemRole && (
                          <Shield className="h-3 w-3 text-muted-foreground" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {role.description || ''}
                      </p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-xs text-muted-foreground">
                          {role.permission_count || 0} {t('roles.permissions')}
                        </span>
                        {!role.is_active && (
                          <Badge variant="secondary" className="text-xs">
                            {t('common.inactive')}
                          </Badge>
                        )}
                      </div>
                    </div>
                </label>
                )
              })}
              
              {roles.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">{t('users.noRolesAvailable')}</p>
                </div>
              )}
            </div>
          )}
          {rolesError && <p className="mt-1 text-sm text-destructive flex items-center gap-1">{rolesError}</p>}
        </div>

          </div>
        )

      default:
        return null
    }
  }

  const footer = (
    <div className="flex justify-between items-center">
      <Button 
        variant="outline" 
        onClick={currentStep === 0 ? handleClose : handlePrevious}
        disabled={loading}
        className="min-w-[100px]"
      >
        <ChevronLeft className="w-4 h-4 mr-2" />
        {currentStep === 0 ? t('users.form.cancel') : t('users.form.previous')}
      </Button>
      
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>{t('users.form.step')} {currentStep + 1} {t('common.of')} {totalSteps}</span>
      </div>
      
      <div className="flex space-x-3">
        {currentStep < totalSteps - 1 ? (
          <Button 
            onClick={handleNext}
            disabled={loading}
            className="min-w-[100px]"
          >
            {t('users.form.next')}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button 
            onClick={handleSubmit}
            disabled={loading}
            className="min-w-[120px]"
          >
            {loading ? (
              t('common.saving')
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                {t('users.form.saveUser')}
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? t('users.form.editUserTitle') : t('users.form.createUserTitle')}
      footer={footer}
    >
      <div className="space-y-6">
        {renderStepIndicator()}
        
        <div className="min-h-[250px]">
          {renderStepContent()}
        </div>
      </div>
    </Modal>
  )
}
